import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Flame, Trophy, Zap, Sparkles, Share2, Loader2, Eye, EyeOff } from 'lucide-react';
import { cn } from '../../lib/utils';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const STAT_PILL_COLORS = ['text-amber-600 bg-amber-50', 'text-orange-600 bg-orange-50', 'text-emerald-600 bg-emerald-50', 'text-stone-600 bg-stone-50', 'text-stone-600 bg-stone-50'];

export default function WhosHotCard({ teamId, teamName, showToggle = false, isCoach = false, onTogglePersist, compact = false, publicPage = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingAI, setLoadingAI] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [hiddenForTeam, setHiddenForTeam] = useState(false);

  const load = useCallback(async (withAI = false) => {
    if (!teamId) return;
    try {
      const res = await axios.get(`${API}/api/teams/${teamId}/whos-hot${withAI ? '?ai=true' : ''}`);
      setData(res.data);
      setHiddenForTeam(res.data?.enabled === false);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
      setLoadingAI(false);
    }
  }, [teamId]);

  useEffect(() => { load(false); }, [load]);

  const handleToggleOff = async () => {
    if (!window.confirm('Hide the Who\'s Hot card from parents and the public?\n\nYou can turn it back on anytime from this card.')) return;
    try {
      await axios.patch(`${API}/api/teams/${teamId}/whos-hot-toggle`, { show: false }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token') || (JSON.parse(localStorage.getItem('auth-storage')||'{}')?.state?.token || '')}` }
      });
      setHiddenForTeam(true);
      if (onTogglePersist) onTogglePersist(false);
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || 'Failed to toggle');
    }
  };

  const handleToggleOn = async () => {
    try {
      await axios.patch(`${API}/api/teams/${teamId}/whos-hot-toggle`, { show: true }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token') || (JSON.parse(localStorage.getItem('auth-storage')||'{}')?.state?.token || '')}` }
      });
      setHiddenForTeam(false);
      if (onTogglePersist) onTogglePersist(true);
      load(false);
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || 'Failed to toggle');
    }
  };

  const handleGenerateAI = () => {
    setLoadingAI(true);
    load(true);
  };

  const shareHot = async () => {
    const url = `${window.location.origin}/whos-hot/${teamId}`;
    const text = `Who's Hot on ${teamName || data?.team_name || 'our team'} — ${url}`;
    try {
      if (navigator.share) await navigator.share({ title: 'Who\'s Hot', text, url });
      else {
        await navigator.clipboard.writeText(url);
        // eslint-disable-next-line no-alert
        alert('Link copied!');
      }
    } catch {}
  };

  if (dismissed) return null;
  if (loading) return null;

  // Coach toggle — turned off state, only coach sees this
  if (hiddenForTeam) {
    if (!isCoach) return null;
    return (
      <Card className="border-stone-200 bg-stone-50 border-dashed" data-testid="whos-hot-hidden">
        <CardContent className="p-3 flex items-center gap-2 text-sm text-stone-500">
          <EyeOff size={14} />
          <span className="flex-1">Who's Hot card is hidden from parents and the public.</span>
          <Button size="sm" variant="ghost" onClick={handleToggleOn} className="text-amber-700 hover:text-amber-800" data-testid="whos-hot-turn-on">
            Turn back on
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!data || !data.players || data.players.length === 0) {
    // Show friendly empty state only when on the public share page or for coaches.
    if (!publicPage && !isCoach) return null;
    return (
      <Card className="border-stone-200 bg-stone-50" data-testid="whos-hot-empty">
        <CardContent className="p-6 text-sm text-stone-500 text-center">
          <Flame size={28} className="mx-auto mb-3 text-orange-400" />
          <p className="font-semibold text-stone-700 mb-1">Who&apos;s Hot is warming up</p>
          <p className="text-xs">{data?.note || 'Top hitters will appear here once the team completes a few games.'}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      className="border-0 shadow-lg bg-gradient-to-br from-orange-600 via-red-600 to-amber-700 text-white overflow-hidden"
      data-testid="whos-hot-card"
    >
      <CardContent className={cn("p-4", compact && "p-3")}>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
            <Flame size={16} className="animate-pulse" />
          </div>
          <div className="flex-1">
            <p className="font-black tracking-wider text-sm sm:text-base leading-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '0.08em' }}>
              WHO&apos;S HOT
            </p>
            <p className="text-white/80 text-[10px] uppercase tracking-widest">{data.team_name}</p>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={shareHot}
            className="text-white hover:bg-white/20 h-8 w-8 p-0"
            data-testid="whos-hot-share"
            title="Share"
          >
            <Share2 size={14} />
          </Button>
          {showToggle && isCoach && (
            <Button
              size="sm"
              variant="ghost"
              onClick={handleToggleOff}
              className="text-white/80 hover:bg-white/20 h-8 w-8 p-0"
              title="Hide this card"
              data-testid="whos-hot-hide"
            >
              <EyeOff size={14} />
            </Button>
          )}
        </div>

        <div className="space-y-2">
          {data.players.slice(0, compact ? 3 : 5).map((p, idx) => (
            <div key={p.id} className="flex items-center gap-3 bg-white/10 rounded-lg px-3 py-2" data-testid={`whos-hot-player-${idx}`}>
              <div className="shrink-0 w-7 h-7 rounded-full bg-white text-orange-700 flex items-center justify-center font-black text-sm shadow">
                {idx + 1}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold truncate">
                  {p.name} {p.jersey && <span className="text-white/70 font-normal text-xs">#{p.jersey}</span>}
                </p>
                <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                  <span className={cn("text-[10px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider", STAT_PILL_COLORS[idx % STAT_PILL_COLORS.length])}>
                    {p.last5_avg > 0 ? `L5 .${String(Math.round(p.last5_avg * 1000)).padStart(3, '0')}` : `.${String(Math.round(p.season_avg * 1000)).padStart(3, '0')} AVG`}
                  </span>
                  {p.hit_streak >= 2 && (
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider bg-red-700 text-white">
                      🔥 {p.hit_streak}-GAME STREAK
                    </span>
                  )}
                  {p.hr > 0 && (
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider bg-yellow-300 text-yellow-900">
                      <Zap size={8} className="inline mr-0.5" /> {p.hr} HR
                    </span>
                  )}
                  {p.rbi >= 3 && (
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider bg-emerald-600 text-white">
                      <Trophy size={8} className="inline mr-0.5" /> {p.rbi} RBI
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* AI narrative */}
        {data.narrative ? (
          <div className="mt-3 bg-black/20 rounded-lg p-3 border border-white/10" data-testid="whos-hot-narrative">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-white/70 font-bold mb-1">
              <Sparkles size={10} /> AI Scouting Report
            </div>
            <p className="text-white/95 text-sm leading-relaxed italic">{data.narrative}</p>
          </div>
        ) : (
          <Button
            size="sm"
            onClick={handleGenerateAI}
            disabled={loadingAI}
            className="w-full mt-3 bg-white/15 hover:bg-white/25 text-white text-xs border border-white/25"
            data-testid="whos-hot-generate-ai"
          >
            {loadingAI ? (
              <><Loader2 size={12} className="mr-1 animate-spin" /> Writing scouting report…</>
            ) : (
              <><Sparkles size={12} className="mr-1" /> Generate AI scouting report</>
            )}
          </Button>
        )}

        {data.games_played > 0 && (
          <p className="text-white/50 text-[10px] text-center mt-2">
            Based on {data.games_played} completed game{data.games_played === 1 ? '' : 's'}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
